import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import type { BadgeTone } from '@/shared/ui/badge';
import type { AiAvailability } from '@/api/schemas/ai';

/**
 * The five availability states, each with exactly one sentence.
 *
 * These wordings are the whole user-facing story about AI for a tenant, and
 * they are written as sentences a person would say. "AI is ready." is short
 * enough to read at a glance and honest enough to act on; a state named
 * `provider_unavailable` is a fact about the system, not an answer.
 *
 * `ready` is the only state in which AI can actually be used. The other four
 * say so plainly and add the one thing that matters to someone whose work is
 * blocked by it: the MANUAL forms still work. AI is an aid here, never a
 * requirement, and a tenant should never believe that turning AI off stops
 * them working.
 */
export type AvailabilityWording = {
  /** The one sentence that says what is true. */
  readonly sentence: string;
  /** A short label for the badge beside it. */
  readonly label: string;
  readonly tone: BadgeTone;
};

export const AI_AVAILABILITY_WORDING: Record<AiAvailability, AvailabilityWording> = {
  disabled_by_platform: {
    sentence: 'AI is turned off for this workspace by the platform.',
    label: 'Off',
    tone: 'neutral',
  },
  configuration_required: {
    sentence: 'No AI provider is configured yet.',
    label: 'Not configured',
    tone: 'caution',
  },
  provider_unavailable: {
    sentence: 'The configured AI provider is not available right now.',
    label: 'Unavailable',
    tone: 'critical',
  },
  limit_reached: {
    sentence: 'The usage allowance for this period is used up.',
    label: 'Limit reached',
    tone: 'caution',
  },
  ready: {
    sentence: 'AI is ready.',
    label: 'Ready',
    tone: 'positive',
  },
};

/**
 * The availability sentence, plus the assurance that the manual forms still
 * work when AI is not.
 */
export function AvailabilityNotice({
  availability,
  reason,
}: {
  availability: AiAvailability;
  reason?: string | null;
}): ReactNode {
  const wording = AI_AVAILABILITY_WORDING[availability];
  const ready = availability === 'ready';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={wording.tone}>{wording.label}</Badge>
        <p className="text-sm text-content">{wording.sentence}</p>
      </div>

      {reason ? <p className="text-sm text-content-muted">{reason}</p> : null}

      {ready ? null : (
        <p className="text-sm text-content-muted">
          AI is an aid, never a requirement — everything can still be created by hand.
        </p>
      )}
    </div>
  );
}
